"use client";

import { useEffect, useState } from "react";
import SearchableDropdown from "../components/SearchableDropdown";
import { eventsApi } from "@/lib/api";
import { formatCurrency } from "@/lib/data";

interface EventOption {
  id: number;
  name: string;
  status?: string;
  start_date?: string;
  client?: { name?: string };
}

/** What the invoices page receives on save. */
export interface InvoiceFormData {
  /** Event id, when generating a new invoice */
  event: string;
  dueDate: string;
  notes: string;
}

/** The invoice being edited, as shown read-only at the top of the form. */
interface InvoiceSummary {
  number: string;
  client: string;
  event: string;
  total: number;
  paid: number;
  balance: number;
  status: string;
  dueDate: string;
  notes: string;
}

interface InvoiceFormProps {
  onSubmit: (data: InvoiceFormData) => void | Promise<void>;
  onCancel: () => void;
  /** Present when editing; absent when generating a new invoice */
  invoice?: InvoiceSummary;
  /** Events that already have an invoice (hidden from the picker) */
  invoicedEventIds?: number[];
}

/**
 * Invoices come from their event: the amounts are the event's services, discount and
 * GST, and the status follows the payments recorded on the event. So generating only
 * needs the event, and editing only changes the due date and notes.
 */
export default function InvoiceForm({ onSubmit, onCancel, invoice, invoicedEventIds = [] }: InvoiceFormProps) {
  const editing = Boolean(invoice);
  const [eventsList, setEventsList] = useState<EventOption[]>([]);
  const [eventsLoading, setEventsLoading] = useState(!editing);
  const [formData, setFormData] = useState<InvoiceFormData>({
    event: "",
    dueDate: invoice?.dueDate?.slice(0, 10) || "",
    notes: invoice?.notes || "",
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) return;
    let active = true;
    eventsApi.list("per_page=1000")
      .then((res: any) => {
        const list: EventOption[] = res?.data?.data ?? res?.data ?? [];
        if (!active) return;
        const invoiced = new Set(invoicedEventIds);
        setEventsList((Array.isArray(list) ? list : []).filter(ev => ev.status !== "cancelled" && !invoiced.has(ev.id)));
      })
      .catch(() => { if (active) setEventsList([]); })
      .finally(() => { if (active) setEventsLoading(false); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing && !formData.event) {
      setError("Choose the event to invoice");
      return;
    }
    setSaving(true);
    try {
      await onSubmit(formData);
    } finally {
      setSaving(false);
    }
  };

  const input = "w-full h-11 px-3 rounded-xl border border-stone-200 bg-white text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none";

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {invoice ? (
        <div className="rounded-xl border border-stone-200 bg-stone-50 p-3 text-sm">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="font-semibold text-stone-800">{invoice.number}</p>
              <p className="text-xs text-stone-500 truncate">{[invoice.client, invoice.event].filter(Boolean).join(" · ")}</p>
            </div>
            <span className="text-xs font-medium text-stone-600 bg-white border border-stone-200 rounded-full px-2 py-0.5 shrink-0">{invoice.status}</span>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2">
            <div><p className="text-[11px] text-stone-400">Total</p><p className="font-semibold tabular-nums">{formatCurrency(invoice.total)}</p></div>
            <div><p className="text-[11px] text-stone-400">Paid</p><p className="font-semibold text-emerald-600 tabular-nums">{formatCurrency(invoice.paid)}</p></div>
            <div><p className="text-[11px] text-stone-400">Balance</p><p className={`font-semibold tabular-nums ${invoice.balance > 0 ? "text-rose-600" : "text-emerald-600"}`}>{formatCurrency(invoice.balance)}</p></div>
          </div>
          <p className="mt-3 text-xs text-stone-500">
            Amounts come from the event, and the status follows the payments recorded on it. To change them, edit the event or record a payment there.
          </p>
        </div>
      ) : (
        <div>
          <SearchableDropdown
            label="Event *"
            options={eventsList.map(ev => ({
              value: String(ev.id),
              label: [ev.name, ev.client?.name].filter(Boolean).join(" — "),
            }))}
            value={formData.event}
            onChange={(value) => { setFormData(f => ({ ...f, event: value })); setError(""); }}
            placeholder={eventsLoading ? "Loading events..." : "Search and select an event"}
            error={error}
          />
          {!eventsLoading && eventsList.length === 0 ? (
            <p className="text-xs text-stone-500 mt-2">
              Every event already has an invoice. Invoices are created automatically when an event is saved.
            </p>
          ) : (
            <p className="text-xs text-stone-500 mt-2">
              The invoice takes its amounts from the event (services, discount and GST) and is numbered from Settings › Documents.
            </p>
          )}
        </div>
      )}

      {editing && (
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5" htmlFor="invoice-due">Due date</label>
          <input
            id="invoice-due"
            type="date"
            value={formData.dueDate}
            onChange={(e) => setFormData(f => ({ ...f, dueDate: e.target.value }))}
            className={input}
          />
        </div>
      )}

      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5" htmlFor="invoice-notes">Notes on the invoice</label>
        <textarea
          id="invoice-notes"
          value={formData.notes}
          onChange={(e) => setFormData(f => ({ ...f, notes: e.target.value }))}
          rows={3}
          placeholder="Optional"
          className="w-full px-3 py-2.5 rounded-xl border border-stone-200 bg-white text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none resize-none"
        />
      </div>

      <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-1">
        <button type="button" onClick={onCancel} className="h-11 px-4 rounded-xl border border-stone-200 text-stone-600 font-medium hover:bg-stone-50">
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving || (!editing && eventsList.length === 0 && !eventsLoading)}
          className="h-11 px-5 rounded-xl bg-amber-600 text-white font-semibold hover:bg-amber-700 disabled:opacity-50"
        >
          {saving ? "Saving..." : editing ? "Save" : "Generate invoice"}
        </button>
      </div>
    </form>
  );
}
